import React, { useState, useEffect, useCallback } from 'react';

import api from './api';
import Years from './components/Years.js';
import Chances from './components/Chances.js';
import Description from './components/Description.js';
import Subscription from './components/Subscription.js';
import Loading from './components/Loading.js';
import Message, { MessageTypes } from './components/Message.js';
import './App.css';

const pages = {
   home: '',
   payment: 'payment',
   calculation: 'calculation',
   subscription: 'subscription',
   subscriptionPremium: 'subscription/premium',
   subscriptionBasic: 'subscription/basic'
};

// Add this function to generate mock suggestions
function generateMockSuggestions(chances) {
   if (!chances || !Array.isArray(chances) || chances.length === 0) {
      return null;
   }
   
   // Create a deep copy of the chances array to avoid modifying the original
   const mockSuggestions = JSON.parse(JSON.stringify(chances));
   
   // Sort the original chances by highest first
   mockSuggestions.sort((a, b) => b.chance - a.chance);
   
   // Process each item - improve all chances by 10-20%
   mockSuggestions.forEach((item, index) => {
      // Mark as mock data
      item.isMockData = true;
      
      // Store the original chance for reference
      const chance = Math.min(item.chance + (0.01 + Math.random() * 0.1), 1);
      
      // Improve all chances by 10-20%
      const improvement = chance * (0.1 + Math.random() * 0.1);
      const improvedChance = Math.min(chance + improvement, 1);
      
      // Set the improved chance
      item.chance = improvedChance;
      
      // For top 3 items, we need to establish proper hierarchies
      if (index === 0) {
         // First item: bonus3 is highest, others lower
         item.chanceBonus3 = improvedChance;
         item.chanceBonus2 = Math.min(improvedChance / (Math.random() * 0.1 + 1.2)); // Ensure it's lower than bonus3
         item.chanceBonus1 = Math.min(improvedChance / (Math.random() * 0.1 + 1.4)); // Ensure it's lower than bonus2
      } 
      else if (index === 1) {
         // Second item: bonus3 >= bonus2 > bonus1
         item.chanceBonus3 = Math.min(improvedChance * (Math.random() * 0.1 + 1.2), 1);
         item.chanceBonus2 = improvedChance;
         item.chanceBonus1 = Math.min(improvedChance / (Math.random() * 0.1 + 1.2), 1); // Ensure it's lower than bonus2
      }
      else if (index === 2) {
         // Third item: bonus3 >= bonus2 >= bonus1
         item.chanceBonus3 = Math.min(improvedChance * (Math.random() * 0.1 + 1.4), 1);
         item.chanceBonus2 = Math.min(improvedChance * (Math.random() * 0.1 + 1.2), 1); // Ensure it's >= bonus1
         item.chanceBonus1 = improvedChance;
      }
      else {
         // All other items: standard bonus distribution
         item.chanceBonus3 = Math.min(improvedChance * (Math.random() * 0.1 + 1.6), 1);
         item.chanceBonus2 = Math.min(improvedChance * (Math.random() * 0.1 + 1.4), 1);
         item.chanceBonus1 = Math.min(improvedChance * (Math.random() * 0.1 + 1.2), 1);
      }
   });
   // Sort the final list by chance (highest first)
   mockSuggestions.sort((a, b) => b.chance - a.chance);
   
   return mockSuggestions;
}

export default function App() {
   const page = getPage();
   
   const [ state, setState ] = useState({
      page: page || '',
      id: '', // 19000818,
      year: '', // 2019,
      chances: [],
      suggested: null,
      loading: false,
      timestamp: Date.now(),
      paymentId: '',
      paymentSecret: '',
      paymentStatus: '',
      message: {}
   });

   const onHashChange = useCallback(() => {
      const page = getPage();

      setState(state => {
         if (page !== state.page) {
            if (Object.values(pages).includes(page)) {
               if ((page === pages.subscription || page === pages.subscriptionBasic || page === pages.subscriptionPremium) && (!state.id || !state.year)) {
                  window.location.hash = pages.home;
                  return state;
               }
               return { ...state, page };
            }
            window.location.hash = pages.home;
         }
         return state;
      });
   }, []);

   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const paymentId = params.get('payment_intent');
      const paymentSecret = params.get('payment_intent_client_secret');

      window.addEventListener('hashchange', onHashChange);

      if (paymentId && paymentSecret) {
         window.location = `/#${pages.payment}`;
      }
      return () => {
         window.removeEventListener('hashchange', onHashChange);
      }
   }, [ onHashChange ]);

   // Check if user should be allowed on subscription page
   useEffect(() => {
      // Check if current page is a subscription page and id/year are missing
      if ((state.page === pages.subscription || 
           state.page === pages.subscriptionBasic || 
           state.page === pages.subscriptionPremium) && 
          (!state.id || !state.year)) {
         window.location.hash = pages.home;
      }
   }, [ state.page, state.id, state.year ]);

   useEffect(() => {
      if (state.page === pages.home) {
         setState(state => ({ ...state, chances: [] }));
      }
      else if (state.page === pages.payment) {
         const message = { type: MessageTypes.info, text: 'Плащането е извършено успешно.' };
         setState(state => ({ ...state, message }));
      }
   }, [ state.page ]);

   function getPage() {
      return (window.location.hash || '#').substring(1);
   }

   function onYear(year) {
      setState(state => ({ ...state, year }));
   };

   function onId(event) {
      const { value } = event.target;

      if (/^(|\d+)$/.test(value)) {
         setState(state => ({ ...state, id: Number(value) || '' }));
      }
   }

   function onCalculate(event) {
      const currentYear = new Date().getFullYear();

      event.preventDefault();
      window.location.hash = pages.calculation;

      if (!(/^\d{8}$/.test(state.id)) || state.year > currentYear - 1 || state.year < currentYear - 6) {
         const message = { type: MessageTypes.error, text: 'Невалиден набор или ID на дете.' };
         setState(state => ({ ...state, message }));
         return;
      }
      setState(state => ({ ...state, loading: true }));
      calculate(state.id, state.year);
   }

   function calculate(id, year) {
      api.calculate(id, year)
         .then(data => {
            let chances = [];
            let paymentId = '';
            let paymentSecret = '';
            let paymentStatus = '';
            let subscription = '';
            let message = { type: MessageTypes.error, text: 'Проблем с връзката към системата на детските градини.' };
   
            if (data) {
               paymentId = data.paymentId;
               paymentSecret = data.paymentSecret;
               paymentStatus = data.paymentStatus;
               subscription = data.subscription;

               if (data.chances) {
                  chances = data.chances;

                  if (chances.length) {
                     if (paymentStatus === 'succeeded') {
                        const year = new Date(data.paymentCreated * 1000).getFullYear();
                        message = { type: MessageTypes.info, text: `Имате неограничен брой калкулации до края на ${year} година. Благодаря за подкрепата.`, button: 'Абонамент', url: `#${pages.subscription}` };
                     }
                     else {
                        message = { type: MessageTypes.info, text: `Остават ви ${data.calculations} безплатни калкулации. Aбонирайте се, за да премахнете лимита.`, button: 'Абонамент', url: `#${pages.subscription}` };
                     }
                  }
                  else {
                     message = { type: MessageTypes.error, text: 'Няма намерени данни.' };
                  }
               }
               else {
                  message = { type: MessageTypes.error, text: 'Вашите безплатните калкулации са изчерпани. Aбонирайте се, за да премахнете лимита.', button: 'Абонамент', url: `#${pages.subscription}` };
               }
            }

            // Create mock suggestions for free and basic users
            let mockSuggested = null;
            if (chances && chances.length && !data.suggested) {
               // Only generate mock if we have chances but no real suggestions
               mockSuggested = generateMockSuggestions(chances);
            }
            
            setState(state => ({ 
               ...state,
               message,
               chances,
               suggested: data?.suggested || mockSuggested,
               paymentId,
               paymentSecret,
               paymentStatus,
               subscription,
               loading: false,
               timestamp: Date.now()
            }));
         })
         .catch(error => console.log(error));
   }

   return (
      <div className="App">
         <header>
            <a href="/#"><img alt="Малко дете" src="logo.svg" /></a>
            <h1>Калкулатор на шансове</h1>
            <h2>за прием в детски градини в София</h2>
            <form>
               <Years year={state.year} onSelect={onYear} />
               <input type="text" maxLength="8" placeholder="ID на дете" value={state.id} onChange={onId} />
               <button onClick={onCalculate}>Изчисли</button>
            </form>
         </header>
         <main>
            {state.message.type
               ? <Message type={state.message.type} text={state.message.text} button={state.message.button} url={state.message.url} />
               : null
            }
            {(state.page === pages.subscription || 
              state.page === pages.subscriptionBasic || 
              state.page === pages.subscriptionPremium)
               ? <Subscription id={state.id} year={state.year} subscription={state.subscription} page={state.page} pages={pages} />
               : state.page === pages.calculation && state.chances.length
                  ? <Chances timestamp={state.timestamp} chances={state.chances} suggested={state.suggested} subscription={state.subscription} pages={pages} />
                  : <Description />
            }
            {state.loading
               ? <Loading />
               : null
            }
         </main>
      </div>
   );
}